
// src/contexts/PracticeContext.js
import React, { createContext, useContext, useReducer, useEffect } from 'react';

const PracticeContext = createContext();

// Action types
const PRACTICE_ACTIONS = {
  SET_SESSION: 'SET_SESSION',
  UPDATE_GAME_STATE: 'UPDATE_GAME_STATE',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  CLEAR_SESSION: 'CLEAR_SESSION',
  UPDATE_STATS: 'UPDATE_STATS',
  ADD_ACHIEVEMENT: 'ADD_ACHIEVEMENT'
};

// Initial state
const initialState = {
  currentSession: null,
  gameState: null,
  loading: false,
  error: null,
  statistics: {
    totalGames: 0,
    gamesWon: 0,
    gamesLost: 0,
    totalPoints: 0,
    bestScore: 0,
    fastestWin: 0
  },
  achievements: [],
  recentSessions: []
};

// Reducer
function practiceReducer(state, action) {
  switch (action.type) {
    case PRACTICE_ACTIONS.SET_SESSION:
      return {
        ...state,
        currentSession: action.payload,
        error: null
      };
    
    case PRACTICE_ACTIONS.UPDATE_GAME_STATE:
      return {
        ...state,
        gameState: action.payload
      };
    
    case PRACTICE_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload
      };
    
    case PRACTICE_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false
      };
    
    case PRACTICE_ACTIONS.CLEAR_SESSION:
      return {
        ...state,
        currentSession: null,
        gameState: null
      };
    
    case PRACTICE_ACTIONS.UPDATE_STATS:
      return {
        ...state,
        statistics: { ...state.statistics, ...action.payload }
      };
    
    case PRACTICE_ACTIONS.ADD_ACHIEVEMENT:
      return {
        ...state,
        achievements: [...state.achievements, action.payload]
      };
    
    default:
      return state;
  }
}

// Provider component
export function PracticeProvider({ children }) {
  const [state, dispatch] = useReducer(practiceReducer, initialState);

  // Load statistics on mount
  useEffect(() => {
    loadStatistics();
  }, []);

  const loadStatistics = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/games/practice/stats', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          dispatch({ type: PRACTICE_ACTIONS.UPDATE_STATS, payload: data.statistics });
        }
      }
    } catch (error) {
      console.error('Failed to load practice statistics:', error);
    }
  };

  const createPracticeSession = async (difficulty = 'medium', aiCount = 3) => {
    dispatch({ type: PRACTICE_ACTIONS.SET_LOADING, payload: true });
    
    try {
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user'));
      
      const response = await fetch('/api/games/practice/create', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId: user.id,
          username: user.username,
          difficulty,
          aiCount
        })
      });

      const data = await response.json();
      
      if (data.success) {
        dispatch({ 
          type: PRACTICE_ACTIONS.SET_SESSION, 
          payload: { gameId: data.gameId, difficulty, aiCount } 
        });
        dispatch({ 
          type: PRACTICE_ACTIONS.UPDATE_GAME_STATE, 
          payload: data.gameState 
        });
        return data;
      } else {
        throw new Error(data.error || 'Failed to create practice session');
      }
    } catch (error) {
      dispatch({ type: PRACTICE_ACTIONS.SET_ERROR, payload: error.message });
      throw error;
    } finally {
      dispatch({ type: PRACTICE_ACTIONS.SET_LOADING, payload: false });
    }
  };

  const makeMove = async (moveType, data) => {
    if (!state.currentSession) {
      throw new Error('No active practice session');
    }

    try {
      const token = localStorage.getItem('token');
      let endpoint = '';
      let body = { gameId: state.currentSession.gameId, ...data };

      switch (moveType) {
        case 'draw':
          endpoint = '/draw';
          break;
        case 'discard':
          endpoint = '/discard';
          break;
        case 'declare':
          endpoint = '/validate-declaration';
          break;
        case 'drop':
          endpoint = '/drop';
          break;
        case 'auto-arrange':
          endpoint = '/auto-arrange';
          break;
        default:
          throw new Error('Invalid move type');
      }

      const response = await fetch(`/api/games/practice${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      const result = await response.json();
      
      if (result.success) {
        dispatch({ 
          type: PRACTICE_ACTIONS.UPDATE_GAME_STATE, 
          payload: result.gameState 
        });
        return result;
      } else {
        throw new Error(result.error || 'Move failed');
      }
    } catch (error) {
      dispatch({ type: PRACTICE_ACTIONS.SET_ERROR, payload: error.message });
      throw error;
    }
  };

  const getGameState = async (gameId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/games/practice/${gameId}/state`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        dispatch({ 
          type: PRACTICE_ACTIONS.UPDATE_GAME_STATE, 
          payload: data.gameState 
        });
        return data.gameState;
      } else {
        throw new Error(data.error || 'Failed to get game state');
      }
    } catch (error) {
      dispatch({ type: PRACTICE_ACTIONS.SET_ERROR, payload: error.message });
      throw error;
    }
  };

  const clearSession = () => {
    dispatch({ type: PRACTICE_ACTIONS.CLEAR_SESSION });
  };

  const value = {
    ...state,
    createPracticeSession,
    makeMove,
    getGameState,
    clearSession,
    loadStatistics
  };

  return (
    <PracticeContext.Provider value={value}>
      {children}
    </PracticeContext.Provider>
  );
}

// Hook to use practice context
export function usePractice() {
  const context = useContext(PracticeContext);
  if (!context) {
    throw new Error('usePractice must be used within a PracticeProvider');
  }
  return context;
}

export default PracticeContext;